import { flow, types } from 'mobx-state-tree';

import { getSingle, ContentTypes } from 'core/helpers/prismic';

// Affinity-driven co-branding for staff-fe (issue #30878).
//
// A staff user authenticated with a partner affinity code (Cognito `custom:affinity_code`,
// surfaced as session.user.affinityCode) gets that partner's logo rendered alongside the
// Branch logo in the header. Partner logos live in the shared `quote-partner-data` Prismic
// document (the same one quote-fe's getPartnerAssets reads), so onboarding a new partner is
// a content change, never a code change. Branch-internal users have no affinity code and
// resolve to no partner, leaving the existing experience untouched.
//
// Reference mechanism (quote-fe Plane 2): SPIKE #30283. Staff audit: SPIKE #30281.

const CACHE_KEY_PREFIX = 'staff:co-branding:partner-assets';

const getLocalStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

export const CoBrandingStore = types
  .model({
    loading: types.boolean,
    // Resolved partner asset for the authenticated user's affinity code, or null when the
    // user has no affinity code or no matching partner exists in Prismic.
    partnerAssets: types.maybeNull(types.frozen())
  })
  .actions((self) => ({
    fetchPartnerAssets: flow(function* fetchPartnerAssets(affinityCode) {
      if (self.loading) {
        return;
      }

      const normalizedCode = affinityCode?.toUpperCase() || null;
      const currentCode = self.partnerAssets?.affinity?.toUpperCase() ?? null;

      if (currentCode === normalizedCode) {
        return;
      }
      self.partnerAssets = null;
      if (!normalizedCode) {
        return;
      }

      const cacheKey = `${CACHE_KEY_PREFIX}:${normalizedCode}`;
      const localStorage = getLocalStorage();

      const cached = localStorage?.getItem(cacheKey);
      if (cached) {
        try {
          const { assets, expDate } = JSON.parse(cached);
          if (expDate && new Date(expDate).getTime() > Date.now()) {
            self.partnerAssets = assets;
            return;
          }
        } catch {
          // fall through to a fresh fetch on malformed/stale cache
        }
        localStorage.removeItem(cacheKey);
      }

      self.loading = true;
      try {
        const content = yield getSingle(ContentTypes.PartnerAssets);
        const match = content?.data?.body?.find(
          (item) => item?.primary?.affinity_code?.toUpperCase() === normalizedCode
        );
        if (match) {
          const { primary } = match;
          const assets = {
            affinity: primary.affinity_code,
            name: primary.name?.[0]?.text,
            imgSrc: primary.logo?.url,
            imgAlt: primary.logo?.alt,
            includeInHeader: primary.include_in_header
          };
          self.partnerAssets = assets;
          // Rolling 24h cache, matching quote-fe's getPartnerAssets TTL.
          const expDate = new Date();
          expDate.setDate(expDate.getDate() + 1);
          localStorage?.setItem(cacheKey, JSON.stringify({ assets, expDate: expDate.getTime() }));
        }
      } catch (error) {
        console.error(`Failed to get partner co-branding assets: ${error.message}`);
      } finally {
        self.loading = false;
      }
    })
  }));
